/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { getSettings, useSettings } from '@/data/Settings';
import { STLOC_ALL_STORES_DEF_QUERY } from '../constants/storeLocator';
import { ID } from '@/data/types/Basic';
import { error as logError } from '@/data/utils/loggerUtil';
import { StorelocatorStorelocator } from 'integration/generated/transactions/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize } from 'swr';
import { getUser } from '../User';
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { shrink, expand as expandProps } from '@/data/utils/keyUtil';
import { getLocalization } from '@/data/Localization-Server';
import { getChildContentItems } from '@/data/utils/getChildContentItems';
import { getContentRecommendation } from '@/data/Content/ContentRecommendation-Server';
import { ContentProps } from '@/data/types/ContentProps';
import { STORE_ID } from '../config/STORE_ID';
import { Translation } from 'integration/generated/translations';
import { useLocalization } from '@/data/Localization';
import { createDisplayStoreDetails } from '../utils/storeUtil';
import { customTransactionsStoreLocator } from 'integration/generated/customTransactions';
import { findStoreAttributeByNameStartsWith } from '../utils/StoreStock';

const DATA_KEY = 'DATA_KEY_SOTRE_LOCATOR_CITY_PAGE';

const dataMap = (data: StorelocatorStorelocator, storeLocatorRoute?: string) => {
	const stores = data?.PhysicalStore?.map((ps) => createDisplayStoreDetails(ps, storeLocatorRoute));
	const cityGroupAttribute = findStoreAttributeByNameStartsWith(stores?.at(0)?.attributes,'citygroup')
	return {
		stores:stores??[],
		cityName: cityGroupAttribute?.at(0)?.displayValue??"",
		isRedirect: stores && stores?.length>0?false:true
	}
}

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
		async (
			props: {
				storeId: string;
				cityToken:string;
				query: {
					[key: string]: string | boolean | ID[] | number;
				}
			},
			params: RequestParams
		): Promise<any | undefined> => {
			
			try {
				const { storeId, cityToken, query } = props
				const cityTokenQ = "speelgoedwinkel-"+cityToken;
				const data = await customTransactionsStoreLocator(pub).storeLocatorFindStoresByCity(
					storeId,
					cityTokenQ,
					query,
					params
				);
				return data;
				
	
			} catch (error) {
				logError(context?.req, 'StoreLocator: fetcher: error: %o', error);
			}
		};

export const getStoreLocatorCityPage = async ({ cache, id, context, properties }: ContentProps) => {
	const path = context?.params?.path;
	const cityToken = path?.at(-1)?.replace('speelgoedwinkel-','')??"";
	const user = await getUser(cache, context);
	const settings = await getSettings(cache, context);
	const { StoreLocator } = await getLocalization(cache, context.locale || 'en-US', 'Routes');
	const storeLocatorRoute = (StoreLocator as Translation)?.route as string;

	const props = {
		storeId: settings?.storeId ?? STORE_ID,
		cityToken:cityToken.trim(),
		query: STLOC_ALL_STORES_DEF_QUERY
	};

	const cacheScope = getServerCacheScope(context, user.context);
	const key = unstable_serialize([shrink(props), DATA_KEY]);
	const value = cache.get(key, cacheScope) || dataMap(await fetcher(false, context)(props, {}), storeLocatorRoute);
	const citynameDB = (await value)?.cityName??"";
	await Promise.all([
		getLocalization(cache, context.locale || 'nl-NL', 'StoreLocator'),
		...getChildContentItems(id).map((contentProperties) =>{
			contentProperties = {...contentProperties, emsName:citynameDB+contentProperties.emsName}
			return getContentRecommendation({
				cache,
				id,
				context,
				properties: { ...properties, ...contentProperties },
			})
		})
	]);
	cache.set(key, Promise.resolve(value), cacheScope);
	return value;
}

export const useStoreLocatorCityPage = (cityToken?:string) => {
	const { settings } = useSettings();
	const { StoreLocator: storeLocatorRoute } = useLocalization('Routes');
	const { data, isLoading } = useSWR(
		cityToken? [
			shrink({
				storeId: settings?.storeId ?? STORE_ID,
				cityToken,
				query: STLOC_ALL_STORES_DEF_QUERY
			}),
			DATA_KEY,
		] : null
		,
		async ([props]) => dataMap(await fetcher(false)(expandProps(props), {}), storeLocatorRoute?.route.t())
	);

	return {
		stores: data?.stores??[],
		isRedirect:data?.isRedirect??false,
		cityName:data?.cityName??'',
		isLoading
	}
};